import { Component, inject } from '@angular/core';

import { environment } from '../../../environments/environment';
import { RoleAccessService } from '../../services/role-access.service';
import { isMerchantRoleCode, RoleCode, type RoleCodeValue } from '../../utils/role-access';
import { MERCHANT_PORTAL_NOTICE, merchantPortalUrl } from '../../utils/merchant-portal';
import { DashboardSystemAdminComponent } from './dashboard-system-admin.component';
import { DashboardSystemDeveloperComponent } from './dashboard-system-developer.component';
import { DashboardSystemFinancialComponent } from './dashboard-system-financial.component';

/**
 * Home dashboard: one shell per system role. Merchant roles see a portal redirect notice.
 */
@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [
    DashboardSystemAdminComponent,
    DashboardSystemFinancialComponent,
    DashboardSystemDeveloperComponent,
  ],
  template: `
    @if (effectiveRole(); as role) {
      @if (isMerchantRole(role)) {
        <div class="max-w-lg px-6 py-8">
          <h1 class="m-0 mb-3 text-2xl font-bold text-fg">Use the Merchant Portal</h1>
          <p class="m-0 mb-4 leading-normal text-muted">{{ notice }}</p>
          <p class="m-0 mb-4 leading-normal text-muted">
            <a class="font-semibold text-primary underline" [href]="portalUrl" rel="noopener noreferrer">Open Merchant Portal</a>
          </p>
        </div>
      } @else {
        @switch (role) {
          @case (RoleCode.SYSTEM_ADMIN) {
            <app-dashboard-system-admin />
          }
          @case (RoleCode.SYSTEM_FINANCIAL) {
            <app-dashboard-system-financial />
          }
          @case (RoleCode.SYSTEM_DEVELOPER) {
            <app-dashboard-system-developer />
          }
          @default {
            <app-dashboard-system-admin />
          }
        }
      }
    } @else {
      <p class="p-6 text-muted">Unable to resolve your role. Please re-login.</p>
    }
  `,
})
export class DashboardPageComponent {
  private readonly roleAccess = inject(RoleAccessService);

  readonly RoleCode = RoleCode;
  readonly notice = MERCHANT_PORTAL_NOTICE;
  readonly portalUrl = merchantPortalUrl() || environment.merchantPortalUrl;

  effectiveRole(): RoleCodeValue | null {
    return this.roleAccess.effectiveRole();
  }

  isMerchantRole(role: RoleCodeValue): boolean {
    return isMerchantRoleCode(role);
  }
}
